import React from "react";
import { Box, Chip, Tooltip } from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import PageHeader from "../../components/PageHeader";
import Actions from "../../components/Actions";
import ClientPackagesDialog from "../../components/classes/ClientPackagesDialog";
import AssignPackageDialog from "../../components/classes/AssignPackageDialog";
import { getClientsPage } from "../../services/ClientsService";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { getClientPackagesSummary } from "../../services/PackagesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatTableDate } from "../../utils/tableDateFormat";
import { IAction } from "../../types/table/interfaces.type";

type ClientRow = {
  id: string;
  full_name: string;
  phone: string;
  email: string;
  status: string;
  active_package_count: number;
  next_expiry: string | null;
  active_package_names: string[];
};

const CLIENT_PACKAGES_PAGE_SIZE = 30;

/**
 * Every client in the business, so staff can look up someone's package
 * history without already knowing which class or package they bought.
 * Active-package count and next expiry come from one rollup call per page
 * so the table doesn't fire one request per row.
 */
const ClientPackages: React.FC = () => {
  const params = useParams<{ businessId?: string }>();
  const businessId = params.businessId;

  const [rows, setRows] = React.useState<ClientRow[]>([]);
  const [search, setSearch] = React.useState("");
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);
  const [historyClientId, setHistoryClientId] = React.useState<string | null>(
    null
  );
  const [assignPackageClient, setAssignPackageClient] =
    React.useState<ClientRow | null>(null);

  const debouncedSearch = useDebouncedValue(search, 400);

  // One page of clients from the server, then the package rollup for just
  // the clients on that page.
  const fetchClientsPage = React.useCallback(
    async (tableParams?: any) => {
      const page = Number(tableParams?.page) || 0;
      const size = Number(tableParams?.size) || CLIENT_PACKAGES_PAGE_SIZE;
      const empty = { content: [], number: page, size, totalElements: 0, totalPages: 1 };
      if (!businessId) return empty;
      try {
        const clientsPage = await getClientsPage({
          page,
          size,
          search: String(tableParams?.search || ""),
        });
        const clients = clientsPage?.content ?? [];
        const clientIds = clients
          .map((client: any) => String(client.id ?? ""))
          .filter(Boolean);
        const summary =
          clientIds.length > 0
            ? await getClientPackagesSummary(clientIds).catch(() => [])
            : [];
        const summaryByClientId = new Map(
          summary.map((row) => [String(row.client_id), row])
        );

        const mapped: ClientRow[] = clients.map((client: any) => {
          const id = String(client.id ?? client.user_id ?? client.user?.id ?? "");
          const clientSummary = summaryByClientId.get(id);
          return {
            id,
            full_name: client.full_name || client.name || "-",
            phone: client.phone || "-",
            email: client.user?.email || client.email || "-",
            status: (client.isBanned ?? client.is_banned)
              ? "Banned"
              : client.active
              ? "Verified"
              : "Unverified",
            active_package_count: clientSummary?.active_package_count ?? 0,
            next_expiry: clientSummary?.next_expiry ?? null,
            active_package_names: (clientSummary?.active_packages || []).map(
              (pkg) => pkg.name
            ),
          };
        });
        const content = mapped.filter((row) => row.id);
        setRows(content);
        return { ...empty, ...clientsPage, content };
      } catch (error) {
        toast.error(getErrorMessage(error, "Failed to fetch clients."));
        setRows([]);
        throw error;
      }
    },
    [businessId]
  );

  const findClient = (id: string | number) =>
    rows.find((row) => String(row.id) === String(id)) || null;

  const assignPackageAction: IAction<any> = {
    id: "assignPackage",
    label: "Assign Package",
    name: "Assign Package",
    icon: (
      <Tooltip title="Assign a package">
        <ShoppingCartIcon sx={{ color: "#2e7d32", width: 23 }} />
      </Tooltip>
    ),
    handler: ({ id }: any) => setAssignPackageClient(findClient(id)),
  };

  const actions = [
    ...Actions({
      onViewHistory: {
        handler: (id) => setHistoryClientId(String(id)),
      },
    }),
    assignPackageAction,
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <PageHeader
          title="Client Packages"
          subtitle="Packages your clients have bought."
        />
        <SearchHeaderContainer search={search} setSearch={setSearch} />
        <CustomTable<ClientRow>
          filters={{
            page: 0,
            size: CLIENT_PACKAGES_PAGE_SIZE,
            search: debouncedSearch,
          }}
          refreshSignal={refetchTrigger}
          fields={[
            { key: "full_name", header: "Client" },
            { key: "phone", header: "Phone" },
            { key: "email", header: "Email" },
            { key: "status", header: "Status" },
            {
              key: "active_package_count",
              header: "Active Packages",
              render: (row) => {
                if (row.active_package_count === 0) {
                  return <span style={{ color: "#999" }}>0</span>;
                }
                const label =
                  row.active_package_count === 1
                    ? row.active_package_names[0] || "1 package"
                    : "···";
                return (
                  <Tooltip
                    title={
                      row.active_package_count > 1
                        ? `${row.active_package_count} active packages — click to view`
                        : "Click to view details"
                    }
                  >
                    <Chip
                      size="small"
                      label={label}
                      onClick={() => setHistoryClientId(row.id)}
                      sx={{
                        bgcolor: "#e8f0fd",
                        color: "#2e6fd9",
                        fontWeight: 600,
                        cursor: "pointer",
                        maxWidth: 180,
                        "& .MuiChip-label": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                  </Tooltip>
                );
              },
            },
            {
              key: "next_expiry",
              header: "Next Expiry",
              render: (row) =>
                row.next_expiry ? formatTableDate(row.next_expiry) : "-",
            },
          ]}
          actions={actions}
          fetchFunc={fetchClientsPage}
        />

        <ClientPackagesDialog
          open={Boolean(historyClientId)}
          businessId={businessId}
          initialClientId={historyClientId || undefined}
          onClose={() => setHistoryClientId(null)}
        />

        <AssignPackageDialog
          open={Boolean(assignPackageClient)}
          businessId={businessId}
          clientId={assignPackageClient?.id || null}
          clientName={assignPackageClient?.full_name}
          onClose={() => setAssignPackageClient(null)}
          onAssigned={() => setRefetchTrigger((prev) => prev + 1)}
        />
      </Box>
    </Box>
  );
};

export default ClientPackages;
